import BaseScene from "../../scenes/BaseScene";
import { QuestionModel } from "../../interfaces/IContentModel";
import { PHShape } from "../../plugins/global/PlaceholderPlugin";

export default class QuestionPopupComponent extends Phaser.GameObjects
  .Container {
  background: Phaser.GameObjects.Image;
  model: QuestionModel[];
  q2model: QuestionModel[];
  completeEvent: string;
  scene: BaseScene;
  qSubmitted: boolean;

  questionText: Phaser.GameObjects.Text;
  answersContainer: Phaser.GameObjects.Container;
  tfaContainer: Phaser.GameObjects.Container;
  alertBg: Phaser.GameObjects.Graphics;
  beadGraphics: Phaser.GameObjects.Graphics;
  bgs: Phaser.GameObjects.Image[] = [];
  submitButton: Phaser.GameObjects.Image;
  submitText: Phaser.GameObjects.Text;
  _selected: number = -1;
  currentQNum: number = 0;
  totalCorrectAnswers: number = 0;
  twoFactorAuth: boolean;
  beadArray: Phaser.GameObjects.Image[] = [];

  constructor(
    scene: BaseScene,
    firstQuestionModel: QuestionModel[],
    secondQuestionModel: QuestionModel[],
    completeEventName: string = "question-complete",
    twoFactorQuestion: boolean
  ) {
    super(scene, scene.cameras.main.centerX, scene.cameras.main.centerY + 160);
    this.model = firstQuestionModel;
    this.q2model = secondQuestionModel;
    this.completeEvent = completeEventName;
    this.twoFactorAuth = twoFactorQuestion;
    this.totalCorrectAnswers = 0;
    this.scene = scene;
    this.qSubmitted = false;

    const blackout = scene.sponge.placeholder.image(
      scene,
      0,
      0,
      PHShape.RECT,
      {
        width: 608,
        height: 842,
        radius: 0,
      },
      0x000624
    );

    blackout.alpha = 0.8;

    scene.add.tween({
      targets: blackout,
      alpha: 0.7,
      yoyo: true,
      delay: 1000,
      duration: 6000,
    });

    this.background = scene.add.image(
      0,
      0,
      "lock-door-atlas",
      "question-background.png"
    );

    this.currentQNum = 0;

    this.questionText = scene.add.text(
      0,
      -230,
      this.model[this.currentQNum].question,
      scene.sponge.data.model.fonts.question
    );

    this.questionText.setOrigin(0.5, 0).setFontSize(28);

    this.add([blackout, this.background, this.questionText]);

    this.alertBg = scene.add.graphics();
    this.alertBg.fillStyle(0x740818, 0.8);
    this.add(this.alertBg);

    for (let i = 0; i < 5; i++) {
      const bead = scene.add
        .image(-28 + 54 * i, -301, "lock-door-atlas", "bead.png")
        .setAlpha(0);
      this.beadArray.push(bead);
      this.add(bead);
    }

    this.beadGraphics = scene.add.graphics();
    this.add(this.beadGraphics);

    this.beadArray[0].setAlpha(1);

    this.answersContainer = scene.add.container(0, 0);
    this.add(this.answersContainer);

    this.populateAnswers();

    this.submitButton = scene.add.image(
      0,
      320,
      "lock-door-atlas",
      "submit-box.png"
    );
    this.submitButton.on("pointerup", () => {
      this.submitAnswer(
        this.model[this.currentQNum].answers[this._selected]._correct
      );
    });
    this.submitButton.on("pointerover", () => {
      if (this.submitButton.active) {
        this.submitButton.setFrame(
          this.qSubmitted ? "submit-active-hover.png" : "submit-active.png"
        );
      }
    });

    this.submitButton.on("pointerout", () => {
      if (this.submitButton.active) {
        this.submitButton.setFrame(
          this.qSubmitted ? "submit-active.png" : "submit-box.png"
        );
      }
    });

    this.submitButton.setActive(false);

    this.submitText = scene.add.text(
      0,
      320,
      this.scene.sponge.data.content.strings.submit,
      scene.sponge.data.model.fonts.answer
    );
    this.submitText.setOrigin(0.5);
    this.submitText.setAlpha(0.5);

    this.add([this.submitButton, this.submitText]);

    if (this.twoFactorAuth) {
      //create display to indicate 2 step process
      this.tfaContainer = scene.add.container(0, -500);
      const blackoutBg = scene.sponge.placeholder
        .image(
          scene,
          0,
          0,
          PHShape.RECT,
          {
            width: 608,
            height: 160,
            radius: 0,
          },
          0x000624
        )
        .setAlpha(0.7);

      const label = scene.add
        .text(
          10,
          0,
          this.scene.sponge.data.content.strings.doorOff,
          scene.sponge.data.model.fonts.answer
        )
        .setOrigin(1, 0.5)
        .setAlign("right")
        .setAlpha(0.5);
      const toggleImage = scene.add.image(
        30,
        0,
        "lock-door-atlas",
        "tfaOff.png"
      );
      this.tfaContainer.add([blackoutBg, label, toggleImage]);
      this.tfaContainer.setData("toggle", toggleImage);
      this.tfaContainer.setData("label", label);
      this.add(this.tfaContainer);
    }

    scene.add.existing(this);
  }

  start() {
    if (this.model[this.currentQNum]._voiceOver) {
      this.scene.sponge.startVO(
        this.model[this.currentQNum]._voiceOver,
        this.scene
      );
    }
  }

  populateAnswers() {
    this.bgs = [];
    for (let i = 0; i < this.model[this.currentQNum].answers.length; i++) {
      const ans = this.model[this.currentQNum].answers[i];
      const bg = this.scene.add.image(
        0,
        80 + i * 125,
        "lock-door-atlas",
        "answer.png"
      );
      bg.setData("isSelected", false);

      const text = this.scene.add.text(
        0,
        80 + i * 125,
        ans.text,
        this.scene.sponge.data.model.fonts.answer
      );
      text.setOrigin(0.5);

      this.bgs.push(bg);
      bg.setInteractive({ useHandCursor: true });
      bg.on("pointerup", () => {
        this.selectAnswer(i);
      });

      bg.on("pointerover", () => {
        bg.setFrame("answer-selected.png");

        // if(this.model[this.currentQNum]._voiceOver){
        //   this.scene.sponge.startVO((i==0?"yes":"no"),this.scene);
        // }
      });
      bg.on("pointerout", () => {
        if (!bg.data.values.isSelected) {
          bg.setFrame("answer.png");
        }
      });

      this.answersContainer.add([bg, text]);
    }
  }

  selectAnswer(index: number) {
    this._selected = index;
    this.qSubmitted = true;

    for (let i = 0; i < this.bgs.length; i++) {
      this.bgs[i].setTexture(
        "lock-door-atlas",
        i === index ? "answer-selected.png" : "answer.png"
      );
      this.bgs[i].data.values.isSelected = i === index ? true : false;
    }

    this.submitButton.setActive(true);
    this.submitButton.setFrame("submit-active.png");
    this.submitText.alpha = 1;

    this.submitButton.setInteractive({ useHandCursor: true });
  }

  submitAnswer(isCorrect: boolean) {
    console.log(isCorrect);

    this.scene.add.tween({
      targets: this.beadArray[this.currentQNum],
      duration: 300,
      scale: 3,
      ease: "Quad.easeIn",
      yoyo: true,
    });

    if (isCorrect) {
      this.totalCorrectAnswers++;
      this.scene.sound.play("correctSFX", { volume: 0.5 });

      if (this.totalCorrectAnswers < 2) {
        //update GUI to indicate one question complete
        this.tfaContainer.data.values.label
          .setText(this.scene.sponge.data.content.strings.doorOn)
          .setAlpha(1);
        this.tfaContainer.data.values.toggle.setTexture(
          "lock-door-atlas",
          "tfaOn.png"
        );
        //show FB or pick another question
        this.model[this.currentQNum].posFB
          ? this.showFeedback(this.model[this.currentQNum].posFB, false, true)
          : this.pickNextQuestion(true);
      } else {
        //send completion
        //show FB or pick another question
        this.model[this.currentQNum].posFB
          ? this.showFeedback(this.model[this.currentQNum].posFB, true, true)
          : this.pickNextQuestion(true);
      }
    } else {
      this.scene.sound.play("incorrectSFX", { volume: 0.75 });
      this.beadArray[this.currentQNum].setTint(0x740818);
      console.log("WRONG!");
      this.alertBg.fillStyle(0x740818, 0.8);
      this.alertBg.fillRect(-265, -383, 530, 766);
      this.alertBg.setAlpha(1);
      this.scene.tweens.add({
        targets: this.alertBg,
        alpha: 0,
        duration: 500,
        delay: 250,
        ease: "Quad.easeOut",
        onComplete: () => {
          this.alertBg.clear();
          this.emit("wrongAnswerGiven");
        },
      });
    }
  }

  incorrectAnimationCompleted() {
    if (this.model[this.currentQNum].negFB) {
      this.showFeedback(this.model[this.currentQNum].negFB, false, false);
    } else {
      this.pickNextQuestion(false);
    }
  }

  showFeedback(fbRef: string, finalQ: boolean, isCorrect: boolean) {
    let buttonLabel = null;

    if (finalQ === true) {
      buttonLabel = this.scene.sponge.data.content.strings.keepRunning;
    } else {
      if (isCorrect === true) {
        buttonLabel = this.scene.sponge.data.content.strings.doorLock;
      } else {
        buttonLabel = this.scene.sponge.data.content.strings.retry;
      }
    }
    this.scene.sponge.popup.launchPopup(
      fbRef,
      () => {
        finalQ ? this.endQuestions() : this.pickNextQuestion(isCorrect);
      },
      buttonLabel
    );
  }

  endQuestions() {
    this.scene.events.emit(this.completeEvent, true);
    this.destroy();
  }

  pickNextQuestion(changeQB: boolean) {
    this.currentQNum++;
    if (this.currentQNum >= this.model.length) {
      //run out of questions = game over
      this.scene.events.emit(this.completeEvent, false);
    } else if (
      this.totalCorrectAnswers < 1 &&
      this.currentQNum >= this.model.length - 1
    ) {
      //run out of questions to get 2 answers in time = game over
      this.scene.events.emit(this.completeEvent, false);
    } else {
      // update on screen question
      this.answersContainer.removeAll().setAlpha(0).setScale(1, 0);
      if (changeQB) {
        // update model to new 2nd questions
        this.model = this.q2model;
      }

      this.questionText
        .setText(this.model[this.currentQNum].question)
        .setAlpha(0)
        .setScale(1, 0);
      this.populateAnswers();

      // Play voice over
      if (this.model[this.currentQNum]._voiceOver) {
        this.scene.sponge.startVO(
          this.model[this.currentQNum]._voiceOver,
          this.scene
        );
      }

      this.submitButton.disableInteractive();
      this.submitButton.setActive(false);
      this.submitButton.setFrame("submit-box.png");
      this.submitText.setAlpha(0.5);

      this.emit("showQuestionComponent");

      this.beadArray[this.currentQNum].setAlpha(1);

      this.showBeadAnimation();

      this.scene.tweens.add({
        targets: [this.questionText, this.answersContainer],
        alpha: 1,
        scaleY: 1,
        duration: 300,
        ease: "Back.easeOut",
      });
    }
  }

  showBeadAnimation() {
    this.beadGraphics.clear();
    this.beadGraphics.setPosition(
      this.beadArray[this.currentQNum].x,
      this.beadArray[this.currentQNum].y
    );
    this.beadGraphics.fillStyle(0x30aee4, 0.3);
    this.beadGraphics.lineStyle(20, 0x30aee4, 0.8);
    this.beadGraphics.strokeCircle(0, 0, 100);
    this.beadGraphics.setScale(0.1).setAlpha(1);

    this.scene.tweens.add({
      targets: this.beadGraphics,
      scale: 2,
      alpha: 0,
      duration: 1500,
      ease: "Quad.easeOut",
    });

    this.scene.sound.play("pingSFX", { volume: 0.5 });
  }

  update() {
    super.update();
  }
}
